/**
 * A project's queue: the tasks ready to be worked across its boards, most urgent first, and the
 * ones a session has claimed. A claim is a lease: the holder line says whether the machine is heard from,
 * and the claim's person, the assignee or an owner can take it over (Appendix F, D244).
 */

import { DISPATCH_ACK_SECONDS, isPersonClaim, type DispatchClaim, type QueueClaimedEntry, type QueueEntry, type TaskClaim, type TeamConfigDocument } from '@aioe/shared';
import { time } from '@aioe/shared/wording/dateTime';
import { ListUl } from '@gravity-ui/icons';
import { useState } from 'react';

import { routes } from '../api/client.ts';
import { timeAgo, useLoad } from '../hooks.ts';
import { TaskCost, useTaskCosts } from './costInsights.tsx';
import { useRootFilter } from './RootFilter.tsx';
import { useLocalBoards } from './trackers.tsx';
import { Button, ChoiceSelect, ConfirmDialog, Empty, ErrorNote, ListBlock, ListGroup, ListRow, StatusMark } from './ui.tsx';
import { VIEW_TONES } from './ui/Art.tsx';

const PRIORITY_TEXT: Record<string, string> = { critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low' };

/** What a dispatched claim says now: sent, started, refused or not answered. */
function claimWords(c: TaskClaim & Partial<DispatchClaim>, deviceName: string): string {
  if (!c.dispatchId) return `claimed by ${deviceName} ${timeAgo(c.at)}`;
  if (c.ack) return c.ack.ok ? `started on ${deviceName} ${timeAgo(c.ack.at)}` : `${deviceName} refused it${c.ack.message ? `: ${c.ack.message}` : ''}`;
  return Date.now() - Date.parse(c.at) > DISPATCH_ACK_SECONDS * 1000 ? `sent to ${deviceName} ${timeAgo(c.at)}, not answered` : `sent to ${deviceName} ${timeAgo(c.at)}`;
}

/** "not heard from for 25 min", from the lease's last renewal. */
function silentWords(since?: string): string {
  const mins = since ? Math.max(0, Math.round((Date.now() - Date.parse(since)) / 60_000)) : 0;
  if (mins < 1) return 'just now';
  if (mins < 120) return `${mins} min`;
  return `${Math.round(mins / 60)} h`;
}

type Claimed = QueueClaimedEntry & { claimedBy: TaskClaim & Partial<DispatchClaim> };

export function QueuePanel({ projectId, owner, me }: { projectId: string; owner: boolean; me?: string }) {
  const tone = VIEW_TONES.projects!;
  const queue = useLoad(() => routes.queue(projectId), [projectId], 15_000);
  const teams = useLoad(() => routes.projectTeams(projectId).catch(() => [] as TeamConfigDocument[]), [projectId]);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState<string>();
  const [teamFor, setTeamFor] = useState<Record<string, string>>({});
  const costs = useTaskCosts(projectId);
  const kept = (useLocalBoards(projectId).data ?? []).filter((b) => b.agents > 0);
  const granted = teams.data ?? [];
  /**
   * The team a task goes to. Only an organisation team that owns the board can work it: when the board's
   * team is a workbench team the task goes to the person's own workbench, and no team is guessed (C-24).
   */
  const teamOf = (e: QueueEntry): string | undefined => (e.teamShared ? (teamFor[e.taskId] ?? e.board) : undefined);
  const send = async (e: QueueEntry) => {
    const teamId = teamOf(e);
    setBusy(e.taskId);
    setError(undefined);
    try {
      await routes.dispatchTask(projectId, { rootId: e.rootId, board: e.board, taskId: e.taskId, ...(teamId ? { teamId } : {}) });
      queue.reload();
    } catch (err) {
      const b = (err as { body?: { message?: string } }).body;
      setError(b?.message ?? (err as Error).message);
    } finally {
      setBusy(undefined);
    }
  };
  /** A person lets go of the task they are working (from here or the phone), or an owner does for them: back to the queue. */
  const releasePerson = async (e: QueueEntry & { claimedBy: TaskClaim }) => {
    setBusy(e.taskId);
    setError(undefined);
    try {
      await routes.takeOverClaim(projectId, e.rootId, e.board, e.taskId, { ifEpoch: e.claimedBy.epoch ?? 0, requeue: true, force: true });
      queue.reload();
    } catch (err) {
      setError(((err as { body?: { message?: string } }).body?.message) ?? (err as Error).message);
      queue.reload();
    } finally {
      setBusy(undefined);
    }
  };
  const [taking, setTaking] = useState<Claimed>();
  const [target, setTarget] = useState<string>('queue');
  // The machines a claim can be sent to are asked for when the dialog opens: the online ones, the newest 50.
  const devices = useLoad(() => (taking ? routes.devicesPage({ online: true, kind: 'any', limit: 50 }).then((a) => a.items).catch(() => []) : Promise.resolve([])), [Boolean(taking)]);
  const takeOver = async (e: Claimed) => {
    setError(undefined);
    try {
      const sendTo = target !== 'queue' ? target : undefined;
      await routes.takeOverClaim(projectId, e.rootId, e.board, e.taskId, { ifEpoch: e.claimedBy.epoch ?? 0, ...(sendTo ? { deviceId: sendTo } : { requeue: true }), ...(!e.stale ? { force: true } : {}) });
      setTaking(undefined);
      queue.reload();
    } catch (err) {
      setError(((err as { body?: { message?: string } }).body?.message) ?? (err as Error).message);
      setTaking(undefined);
      queue.reload();
    }
  };
  const filter = useRootFilter(projectId);
  const ready = (queue.data?.ready ?? []).filter((e) => filter.passes(e.rootId));
  const waiting = (queue.data?.waiting ?? []).filter((e) => filter.passes(e.rootId));
  const claimed = (queue.data?.claimed ?? []).filter((e) => filter.passes(e.rootId));
  // Agents busy on a board a workbench keeps are not idle capacity, and nothing here is waiting for them.
  const busyOnLocal = (() => {
    const by = new Map<string, number>();
    for (const b of kept) by.set(b.deviceName, (by.get(b.deviceName) ?? 0) + b.agents);
    const parts = [...by].map(([device, n]) => `${n} ${n === 1 ? 'agent is' : 'agents are'} working on a board kept on ${device}`);
    return parts.length ? parts.join('; ') : undefined;
  })();
  const row = (e: QueueEntry & { claimedBy?: TaskClaim & Partial<DispatchClaim> } & Partial<Claimed>) => {
    const team = teamOf(e);
    const teamName = team ? (granted.find((t) => t.id === team)?.name ?? e.teamName) : undefined;
    const person = e.forPerson;
    // A person is working it (D247, B-10): theirs, held for no agent; they, or an owner, can let it go.
    const personal = Boolean(e.claimedBy && !e.holder && isPersonClaim(e.claimedBy));
    const controls = (
      <>
        <TaskCost costs={costs} rootId={e.rootId} board={e.board} taskId={e.taskId} />
        {!e.claimedBy && !person && e.teamShared && granted.length > 1 && (
          <ChoiceSelect label="Team" value={team ?? undefined} options={Object.fromEntries(granted.map((t) => [t.id, t.name]))} onChange={(v) => setTeamFor({ ...teamFor, [e.taskId]: v })} />
        )}
        {!e.claimedBy && !person && !e.heldFor && (
          <Button size="sm" variant="secondary" isDisabled={busy === e.taskId} onPress={() => void send(e)}>
            {busy === e.taskId ? 'Sending…' : teamName ? `Send to ${teamName}` : 'Send to my workbench'}
          </Button>
        )}
        {personal && (owner || (me && e.claimedBy!.principal === me)) && (
          <Button size="sm" variant="ghost" isDisabled={busy === e.taskId} onPress={() => void releasePerson(e as QueueEntry & { claimedBy: TaskClaim })}>
            {busy === e.taskId ? 'Releasing…' : 'Release'}
          </Button>
        )}
        {e.claimedBy && e.holder && (owner || (me && e.claimedBy.principal === me)) && (
          <Button size="sm" variant="ghost" onPress={() => { setTarget('queue'); setTaking(e as Claimed); }}>
            Take over
          </Button>
        )}
      </>
    );
    const notes = (e.heldFor && !personal) || (e.claimedBy && e.holder) || (!e.claimedBy && !person && !e.teamShared);
    return (
      <ListRow
        key={`${e.rootId}/${e.board}/${e.taskId}`}
        title={`${e.taskId} ${e.title}`}
        description={
          <span className="block truncate">
            {PRIORITY_TEXT[e.priority] ?? e.priority} · {e.teamName} · {e.rootName}
            {personal && person ? ` · ${person.name} is working on it, since ${timeAgo(e.claimedBy!.at)}` : person ? ` · waiting on ${person.name}` : e.assignee && ` · for ${e.assignee}`}
            {e.specialistId && ` · specialist ${e.specialistName ?? 'a specialist'}`}
            {e.claimedBy && !personal && ` · ${claimWords(e.claimedBy, e.holder?.deviceName ?? e.deviceName ?? 'a machine')}`}
          </span>
        }
        control={controls}
      >
        {notes ? (
          <div className="grid gap-0.5 text-xs text-muted">
            {e.heldFor && !personal ? <StatusMark state="medium" label={e.heldFor} wrap /> : null}
            {e.claimedBy && e.holder ? (
              <span className="flex flex-wrap items-center gap-x-3">
                <StatusMark state={e.stale ? 'medium' : e.holder.online ? 'online' : 'offline'} label={e.stale ? `${e.holder.deviceName} · not heard from for ${silentWords(e.claimedBy.renewedAt ?? e.claimedBy.at)}` : `${e.holder.deviceName} · ${e.holder.online ? 'working' : 'offline, lease still holds'}`} />
                {e.lapsesAt && e.stale && <span>goes back to the queue at {time(e.lapsesAt)}</span>}
              </span>
            ) : null}
            {!e.claimedBy && !person && !e.teamShared ? <span>{e.teamName} is a workbench team, not an organisation team, so no other team can work this. It goes to your own workbench; share the team to send it further.</span> : null}
          </div>
        ) : undefined}
      </ListRow>
    );
  };
  const nothing = Boolean(queue.data) && ready.length === 0 && waiting.length === 0 && claimed.length === 0;
  return (
    <>
      <ListGroup
        title="Queue"
        count={ready.length || undefined}
        actions={filter.control}
        description="Ready to be worked, most urgent first. Send one to a team and the platform picks the machine; a workbench claims a task before it starts."
        empty={nothing && !error && !queue.error ? <Empty tone={tone} icon={<ListUl />} compact title="Nothing is ready" body={busyOnLocal ? `${busyOnLocal}. Tasks that are to do, unclaimed and unblocked on the platform's boards show here.` : 'Tasks that are to do, unclaimed and unblocked show here.'} /> : undefined}
      >
        {error || queue.error ? (
          <ListBlock>
            <ErrorNote error={error ?? queue.error} />
          </ListBlock>
        ) : null}
        {!queue.data && !queue.error ? <ListBlock className="text-sm text-muted">Reading the queue…</ListBlock> : null}
        {queue.data && ready.length === 0 && !nothing ? <ListBlock className="text-sm text-muted">Nothing is ready right now; what is held or claimed is below.</ListBlock> : null}
        {ready.map(row)}
      </ListGroup>
      {waiting.length > 0 ? (
        <ListGroup title="Held" count={waiting.length} description="Waiting on a person, a part elsewhere or a plan, so no agent is offered them.">
          {waiting.map(row)}
        </ListGroup>
      ) : null}
      {claimed.length > 0 ? (
        <ListGroup title="Claimed" count={claimed.length} description="A machine or a person holds each while it works.">
          {claimed.map(row)}
        </ListGroup>
      ) : null}
      <ConfirmDialog
        isOpen={!!taking}
        title={taking ? `Take over ${taking.taskId}?` : 'Take over'}
        confirmLabel="Take over"
        onConfirm={() => taking && takeOver(taking)}
        onCancel={() => setTaking(undefined)}
      >
        {taking && (
          <>
            <p>
              {taking.holder.deviceName} {taking.stale ? `has not been heard from for ${silentWords(taking.claimedBy.renewedAt ?? taking.claimedBy.at)}` : `renewed this claim ${silentWords(taking.claimedBy.renewedAt ?? taking.claimedBy.at)} ago and may still be working`}. If it comes back, its next write is refused.
            </p>
            <p>{taking.wip?.branch ? `The work branch ${taking.wip.branch} was pushed${taking.wip.pushedAt ? ` ${timeAgo(taking.wip.pushedAt)}` : ''}${taking.wip.commit ? ` (${taking.wip.commit.slice(0, 8)})` : ''}, so the work so far is safe.` : 'No work branch was pushed from that machine, so anything it has not saved is lost.'}</p>
            <ChoiceSelect label="Then" value={target} options={{ queue: 'Back to the queue', ...Object.fromEntries((devices.data ?? []).filter((d) => d.online !== false).map((d) => [d.deviceId, `Send to ${d.hostname ?? 'a device'}`])) }} onChange={setTarget} />
          </>
        )}
      </ConfirmDialog>
    </>
  );
}
